/**
 * XAI Chat Progress Indicator Component
 * 
 * Displays real-time progress updates during AI processing.
 * Shows thinking messages with title, description, and optional step.
 * 
 * Styled to match Polaris Chat theme.
 */

import React from "react";
import { Box, Typography, CircularProgress, Fade } from "@mui/material";
import { polarisTheme } from "../theme";
import type { ThinkItemMessage } from "../hooks/useXAIChatProgress";

export interface XAIChatProgressIndicatorProps {
  thinkItem: ThinkItemMessage | null;
  isConnected: boolean;
}

export const XAIChatProgressIndicator: React.FC<
  XAIChatProgressIndicatorProps
> = ({ thinkItem, isConnected }) => {
  // Only show if connected and we have a think item
  if (!isConnected || !thinkItem) {
    return null;
  }

  return (
    <Fade in={true}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          p: 2,
          mb: 2,
          bgcolor: polarisTheme.sunkFills,
          borderRadius: `${polarisTheme.roundedL}px`,
          border: `1px solid ${polarisTheme.strokeDefault}`,
        }}
      >
        <CircularProgress
          size={20}
          sx={{
            mt: 0.5,
            color: polarisTheme.linkText,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: polarisTheme.primaryText,
              mb: 0.5,
            }}
          >
            {thinkItem.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: polarisTheme.secondaryText,
              fontSize: "0.875rem",
            }}
          >
            {thinkItem.description}
          </Typography>
          {thinkItem.step && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: polarisTheme.secondaryText,
                fontStyle: "italic",
              }}
            >
              Step: {thinkItem.step}
            </Typography>
          )}
        </Box>
      </Box>
    </Fade>
  );
};
